import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  Stack,
  Group,
  Button,
  Text,
  Card,
  ThemeIcon,
  Center,
  Loader,
  Modal,
  Select,
  TextInput,
  Alert,
  Box,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Upload, Plus, Users, ChevronsUp, ChevronsDown, AlertCircle } from 'react-feather';
import type { Participant } from '../../../types';
import type { GroupWithMembers, GroupsPagination } from '../../../services/api/groups';
import { groupsApi } from '../../../services/api/groups';
import { getApiErrorMessage } from '../../../services/api/client';
import { participantsApi } from '../../../services/api/participants';
import { GroupCard } from './GroupCard';
import { UnassignedParticipantsSection } from './UnassignedParticipantsSection';
import { CoachesPanel } from './CoachesPanel';
import { AssignCoachModal } from './AssignCoachModal';
import { SendCoachReminderModal } from './SendCoachReminderModal';
import { CoachSelectionBar } from './CoachSelectionBar';
import { AssignCoachConfirmModal } from './AssignCoachConfirmModal';
import { SendAnnouncementsModal } from './SendAnnouncementsModal';
import { AddTimeSlotModal } from './AddTimeSlotModal';
import { EditGroupTimeSlotModal } from './EditGroupTimeSlotModal';
import { GroupCapacityWarningModal } from './GroupCapacityWarningModal';
import { GroupReassignConfirmModal, type GroupReassignInfo } from './GroupReassignConfirmModal';
import { DeleteGroupConfirmModal } from './DeleteGroupConfirmModal';
import { RenameGroupModal } from './RenameGroupModal';
import { DraggableParticipantCard } from './DraggableParticipantCard';
import { GroupsCardView } from './GroupsCardView';
import { MatchingSessionsPanel } from './matchingSessions/MatchingSessionsPanel';
import { OverrideWarning } from './matchingSessions/OverrideWarning';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { useQuery } from '@tanstack/react-query';
import { matchingSessionsApi } from '../../../services/api/matchingSessions';
import {
  useGroupsData,
  useParticipantAssignment,
  useCoachAssignment,
  useGroupDragDrop,
  GroupCalloutBanners,
  transformSessionsForGroupCard,
  formatGroupSchedule,
} from './groups';

import { collectAssignmentWarnings, type CoachAssignmentWarning } from './groups/coachConflictUtils';
import { useEmailToggle } from '../../../hooks';

interface GroupsViewProps {
  cohortId: string;
  groups: GroupWithMembers[];
  participants: Participant[];
  loading: boolean;
  loadingMore?: boolean;
  groupsPagination?: GroupsPagination | null;
  onLoadMore?: () => Promise<void>;
  displayMode?: 'list' | 'card';
  cardsPerRow?: number;
  onUploadGroups: () => void;
  onRefresh: () => void | Promise<void>;
  registerHandlers?: (handlers: { openAddGroupModal: () => void }) => void;
  onViewParticipant?: (userId: string) => void;
  searchValue?: string;
  groupsPrereqsMet?: boolean;
  hasSessionDates?: boolean;
  hasTimeSlots?: boolean;
  onEditCohortDetails?: () => void;
  /** Whether this cohort has a 1:1 coaching modality (MED-620) */
  hasOneOnOneModality?: boolean;
  // MED-778: When false, coach-facing CTAs are hidden/disabled
  coachFeaturesEnabled?: boolean;
  // MED-1161: peer-led cohorts hide coach matching/assignment surfaces
  // (availability panel, per-group coach badge/column, Assign Coach, coach
  // side panel, "Manage Coaches" banner action). Coach-led cohorts unchanged.
  isPeerLed?: boolean;
  // MED-977: configured group-coaching session count (from the cohort's
  // group_coaching modality) — used by group cards as the denominator when
  // no sessions are scheduled yet. Null when the cohort has no group
  // coaching modality or its session_count is unset.
  configuredGroupCoachingSessionCount?: number | null;
}

export function GroupsView({
  cohortId,
  groups,
  participants,
  loading,
  loadingMore = false,
  groupsPagination,
  onLoadMore,
  displayMode = 'list',
  cardsPerRow = 2,
  onUploadGroups,
  onRefresh,
  registerHandlers,
  onViewParticipant,
  searchValue,
  groupsPrereqsMet = true,
  hasSessionDates = true,
  hasTimeSlots = true,
  onEditCohortDetails,
  hasOneOnOneModality = false,
  coachFeaturesEnabled = true,
  isPeerLed = false,
  configuredGroupCoachingSessionCount,
}: GroupsViewProps) {
  const navigate = useNavigate();

  // MED-829 / E23-8: Feature flag gate (AC14). When on, the matching-server
  // integration UI replaces the legacy CSV upload entry points.
  const matchingServerEnabled = useFeatureFlag('matching_server_integration');

  // MED-829 / E23-8 AC13 — surface "override saved" warning at the groups-list
  // level when the most recent completed sync had user_in_valid=true. Reads
  // from the same React Query cache that MatchingSessionsPanel populates, so
  // there's no extra fetch.
  const { data: matchingSessions } = useQuery({
    queryKey: ['matchingSessions', cohortId],
    queryFn: () => matchingSessionsApi.list(cohortId),
    enabled: matchingServerEnabled,
  });
  const latestCompletedWithOverride = useMemo(() => {
    if (!matchingSessions) return null;
    // List is sorted by updated_at DESC on the backend.
    return matchingSessions.find(
      (s) => s.status === 'completed' && s.user_in_valid === true,
    ) ?? null;
  }, [matchingSessions]);
  const showGroupsListOverrideWarning =
    matchingServerEnabled && latestCompletedWithOverride !== null;

  // ─── MED-620: Group reassign confirmation modal state ──────────────
  const [reassignInfo, setReassignInfo] = useState<GroupReassignInfo | null>(null);
  const [reassignModalOpened, { open: openReassignModal, close: closeReassignModal }] =
    useDisclosure(false);
  const [reassignConfirming, setReassignConfirming] = useState(false);

  const handleBeforeAssign = useCallback(
    (pending: { participant: { user_id: string; first_name: string; last_name: string; group_id?: string | null }; targetGroup: GroupWithMembers }): boolean => {
      if (!hasOneOnOneModality) return false;

      const { participant, targetGroup } = pending;
      const currentGroup = participant.group_id
        ? groups.find((g) => g.id === participant.group_id)
        : null;

      setReassignInfo({
        participantName: `${participant.first_name} ${participant.last_name}`,
        participantUserId: participant.user_id,
        currentGroupName: currentGroup?.name ?? null,
        currentCoachName: currentGroup?.coach_name ?? null,
        newGroupName: targetGroup.name,
        newCoachName: targetGroup.coach_name ?? null,
        newGroupId: targetGroup.id,
      });
      openReassignModal();
      return true;
    },
    [hasOneOnOneModality, groups, openReassignModal],
  );

  const handleReassignConfirm = useCallback(async () => {
    if (!reassignInfo) return;
    setReassignConfirming(true);
    try {
      await groupsApi.assignMember(cohortId, reassignInfo.newGroupId, reassignInfo.participantUserId);
      notifications.show({
        title: 'Participant Assigned',
        message: `${reassignInfo.participantName} has been assigned to ${reassignInfo.newGroupName}`,
        color: 'green',
      });
      closeReassignModal();
      setReassignInfo(null);
      await onRefresh();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to assign participant',
        color: 'red',
      });
    } finally {
      setReassignConfirming(false);
    }
  }, [reassignInfo, cohortId, closeReassignModal, onRefresh]);

  const handleReassignCancel = useCallback(() => {
    closeReassignModal();
    setReassignInfo(null);
  }, [closeReassignModal]);

  // ─── Hooks ──────────────────────────────────────────────────────────
  const {
    groupSessionsMap,
    cohortTimeSlots,
    calloutData,
    fetchCalloutData,
    participantMap,
    groupHealthMap,
  } = useGroupsData({ cohortId, groups, participants });

  const participantAssignment = useParticipantAssignment({
    cohortId,
    groups,
    participants,
    onRefresh,
    onBeforeAssign: handleBeforeAssign,
  });

  const coachAssignment = useCoachAssignment({
    cohortId,
    groups,
    onRefresh,
    fetchCalloutData,
  });

  const dragDrop = useGroupDragDrop({ cohortId, groups, onRefresh, onBeforeAssign: handleBeforeAssign });

  // MED-579: Email toggle gating
  const { isEnabled: isEmailEnabled } = useEmailToggle(cohortId);
  // MED-778: Feature toggle supersedes per-feature toggles
  const isCoachActivatedEmailDisabled = !coachFeaturesEnabled || !isEmailEnabled('coaches', 'coach_activated');
  const isCoachAvailabilityEmailDisabled = !coachFeaturesEnabled || !isEmailEnabled('coaches', 'coach_availability_request');

  // ─── Modal disclosures ──────────────────────────────────────────────
  const [assignModalOpened, { open: openAssignModal, close: closeAssignModal }] =
    useDisclosure(false);
  const [addGroupModalOpened, { open: openAddGroupModal, close: closeAddGroupModal }] =
    useDisclosure(false);
  const [assignCoachModalOpened, { open: openAssignCoachModal, close: closeAssignCoachModal }] =
    useDisclosure(false);
  const [sendReminderModalOpened, { open: openSendReminderModal, close: closeSendReminderModal }] =
    useDisclosure(false);
  const [addTimeSlotModalOpened, { open: openAddTimeSlotModal, close: closeAddTimeSlotModal }] =
    useDisclosure(false);
  const [editTimeSlotModalOpened, { open: openEditTimeSlotModal, close: closeEditTimeSlotModal }] =
    useDisclosure(false);
  const [assignCoachConfirmModalOpened, { open: openAssignCoachConfirmModal, close: closeAssignCoachConfirmModal }] =
    useDisclosure(false);
  const [announcementModalOpened, { open: openAnnouncementModal, close: closeAnnouncementModal }] =
    useDisclosure(false);
  const [deleteGroupModalOpened, { open: openDeleteGroupModal, close: closeDeleteGroupModal }] =
    useDisclosure(false);
  const [groupToDelete, setGroupToDelete] = useState<GroupWithMembers | null>(null);
  const [deletingGroup, setDeletingGroup] = useState(false);
  // MED-1294: rename modal state
  const [renameGroupModalOpened, { open: openRenameGroupModal, close: closeRenameGroupModal }] =
    useDisclosure(false);
  const [groupToRename, setGroupToRename] = useState<GroupWithMembers | null>(null);
  // Search is now server-side — groups prop already contains filtered results
  const isSearchActive = Boolean(searchValue?.trim());

  // ─── Assignment warnings (list view confirm modal) ──────────────────
  // MED-978: thread groupSessionsMap so date-level conflicts filter to each
  // group's actual session dates rather than flagging every group in the slot.
  const assignmentWarnings = useMemo<CoachAssignmentWarning[]>(() => {
    if (!coachAssignment.selectedCoachForAssignment || coachAssignment.selectedGroupIdsForAssignment.size === 0) return [];
    const selectedGroups = groups.filter((g) => coachAssignment.selectedGroupIdsForAssignment.has(g.id));
    return collectAssignmentWarnings(coachAssignment.selectedCoachForAssignment, selectedGroups, groupSessionsMap);
  }, [coachAssignment.selectedCoachForAssignment, coachAssignment.selectedGroupIdsForAssignment, groups, groupSessionsMap]);

  // ─── Local UI state ─────────────────────────────────────────────────
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [allExpanded, setAllExpanded] = useState(true);
  const [groupForTimeSlot, setGroupForTimeSlot] = useState<GroupWithMembers | null>(null);

  // Register handlers with parent for sub-header buttons
  useEffect(() => {
    if (registerHandlers) {
      registerHandlers({ openAddGroupModal });
    }
  }, [registerHandlers, openAddGroupModal]);

  // ─── Bridge callbacks (hooks + modals) ──────────────────────────────
  // These thin wrappers connect hook state changes to modal open/close.

  const handleAssignToGroup = useCallback(
    (participant: Parameters<typeof participantAssignment.handleAssignToGroup>[0]) => {
      participantAssignment.handleAssignToGroup(participant);
      openAssignModal();
    },
    [participantAssignment, openAssignModal],
  );

  const handleAddParticipantToGroup = useCallback(
    (group: GroupWithMembers) => {
      participantAssignment.handleAddParticipantToGroup(group);
      // Only open if the hook actually set state (i.e., there are unassigned participants)
      if (participantAssignment.unassignedParticipants.length > 0) {
        openAssignModal();
      }
    },
    [participantAssignment, openAssignModal],
  );

  const handleExecuteAssignment = useCallback(async () => {
    await participantAssignment.executeAssignment();
    closeAssignModal();
  }, [participantAssignment, closeAssignModal]);

  const handleChangeCoach = useCallback(
    (group: GroupWithMembers) => {
      coachAssignment.handleChangeCoach(group);
      openAssignCoachModal();
    },
    [coachAssignment, openAssignCoachModal],
  );

  const handleSendCoachReminder = useCallback(
    (coach: Parameters<typeof coachAssignment.handleSendCoachReminder>[0]) => {
      coachAssignment.handleSendCoachReminder(coach);
      openSendReminderModal();
    },
    [coachAssignment, openSendReminderModal],
  );

  const handleAssignCoachToGroups = useCallback(async () => {
    closeAssignCoachConfirmModal();
    await coachAssignment.handleAssignCoachToGroups();
  }, [coachAssignment, closeAssignCoachConfirmModal]);

  /** Handler for viewing conflicts from the confirm modal warning links */
  const handleAddTimeSlot = useCallback(
    (group: GroupWithMembers) => {
      setGroupForTimeSlot(group);
      openAddTimeSlotModal();
    },
    [openAddTimeSlotModal],
  );

  /** Handler for editing a group's existing time slot (MED-648) */
  const handleEditTimeSlot = useCallback(
    (group: GroupWithMembers) => {
      setGroupForTimeSlot(group);
      openEditTimeSlotModal();
    },
    [openEditTimeSlotModal],
  );

  // ─── Rename group handler (MED-1294) ───────────────────────────────
  const handleRenameGroup = useCallback(
    (group: GroupWithMembers) => {
      setGroupToRename(group);
      openRenameGroupModal();
    },
    [openRenameGroupModal],
  );

  // ─── Delete group handlers ─────────────────────────────────────────
  const handleDeleteGroup = useCallback(
    (group: GroupWithMembers) => {
      setGroupToDelete(group);
      openDeleteGroupModal();
    },
    [openDeleteGroupModal],
  );

  const handleConfirmDeleteGroup = useCallback(async () => {
    if (!groupToDelete) return;
    setDeletingGroup(true);
    try {
      await groupsApi.delete(cohortId, groupToDelete.id);
      notifications.show({
        title: 'Group Deleted',
        message: `"${groupToDelete.name}" has been deleted`,
        color: 'green',
      });
      closeDeleteGroupModal();
      setGroupToDelete(null);
      await onRefresh();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete group',
        color: 'red',
      });
    } finally {
      setDeletingGroup(false);
    }
  }, [cohortId, groupToDelete, closeDeleteGroupModal, onRefresh]);

  // ─── Concern flag handlers ─────────────────────────────────────────
  const handleResolveMemberFlag = useCallback(
    async (participantId: string, flagId: string) => {
      try {
        await participantsApi.resolveConcernFlag(cohortId, participantId, flagId);
        notifications.show({
          title: 'Flag Resolved',
          message: 'Concern flag has been marked as resolved',
          color: 'green',
        });
        await onRefresh();
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to resolve flag',
          color: 'red',
        });
      }
    },
    [cohortId, onRefresh],
  );

  const handleDismissMemberFlag = useCallback(
    async (participantId: string, flagId: string) => {
      try {
        await participantsApi.dismissConcernFlag(cohortId, participantId, flagId);
        notifications.show({
          title: 'Flag Dismissed',
          message: 'Concern flag has been dismissed',
          color: 'green',
        });
        await onRefresh();
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to dismiss flag',
          color: 'red',
        });
      }
    },
    [cohortId, onRefresh],
  );

  // ─── Create group handler ──────────────────────────────────────────
  const handleCreateGroup = useCallback(async () => {
    if (!newGroupName.trim()) {
      notifications.show({
        title: 'Error',
        message: 'Please enter a group name',
        color: 'red',
      });
      return;
    }

    setCreatingGroup(true);
    try {
      await groupsApi.create(cohortId, { name: newGroupName.trim() });
      notifications.show({
        title: 'Group Created',
        message: `${newGroupName} has been created`,
        color: 'green',
      });
      setNewGroupName('');
      closeAddGroupModal();
      await onRefresh();
    } catch (error) {
      // MED-1294 (AC8): the create path gets the same conflict wording as rename.
      // The server already rejects duplicates with a 409, but its message is the
      // generic shared one, which doesn't say what collided.
      const status = (error as { status?: number }).status;
      notifications.show({
        title: 'Error',
        message:
          status === 409
            ? `A group named "${newGroupName.trim()}" already exists in this cohort. Group names are case-insensitive, so a different capitalization won't work either.`
            : getApiErrorMessage(error, 'Failed to create group'),
        color: 'red',
      });
    } finally {
      setCreatingGroup(false);
    }
  }, [cohortId, newGroupName, closeAddGroupModal, onRefresh]);

  // ─── Loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  // ─── Empty state ───────────────────────────────────────────────────
  if (groups.length === 0) {
    // MED-363: Build blocking message for prereqs warning
    const getBlockingMessage = () => {
      const missingDates = !hasSessionDates;
      const missingSlots = !hasTimeSlots;
      if (missingDates && missingSlots) {
        return 'Session dates and time slots are not configured for this cohort. Configure these before working with groups.';
      }
      if (missingDates) {
        return 'Session dates are not configured for this cohort. Add session dates before working with groups.';
      }
      if (missingSlots) {
        return 'Time slots are not configured for this cohort. Add time slots before working with groups.';
      }
      return '';
    };

    return (
      <>
        {/* MED-829 / E23-8: matching-server integration UI (gated by feature flag, AC14).
            Panel renders above the empty-state Alert + Card so admins always see the
            Start Matching CTA and any in-flight session rows. */}
        {matchingServerEnabled && (
          <MatchingSessionsPanel
            cohortId={cohortId}
            groupsPrereqsMet={groupsPrereqsMet}
          />
        )}

        {/* MED-363: Prerequisites warning callout */}
        {!groupsPrereqsMet && (
          <Alert
            icon={<AlertCircle size={18} />}
            color="yellow"
            variant="light"
            title={null}
            mb="md"
            mt={matchingServerEnabled ? 'md' : undefined}
          >
            <Stack gap="sm">
              <Text size="sm">{getBlockingMessage()}</Text>
              <Box>
                <Button
                  variant="light"
                  color="yellow"
                  size="xs"
                  onClick={onEditCohortDetails}
                >
                  Edit Cohort Details
                </Button>
              </Box>
            </Stack>
          </Alert>
        )}

        <Card withBorder p="xl">
          <Stack align="center" gap="md" py="xl">
            <ThemeIcon size={80} radius={80} color="gray" variant="light">
              <Users size={40} />
            </ThemeIcon>
            <Text size="xl" fw={600}>
              No Groups Yet
            </Text>
            <Text c="dimmed" ta="center" maw={400}>
              Groups can be created by running Start Matching, uploading a CSV of groups you&apos;ve
              already matched, or creating them manually and assigning participants yourself.
            </Text>
            <Group gap="md">
              {/* MED-1291: this button used to be wrapped in `!matchingServerEnabled`
                  (MED-829 / E23-8 AC5), on the assumption that CSV import was being
                  retired. The client has since confirmed it is a permanent alternative,
                  and the toolbar's equivalent button was never gated — so the same
                  capability was hidden here and visible directly above, which read as
                  the feature having been removed. Ungated now, matching the toolbar.
                  The Tooltip mirrors the toolbar's so a disabled button explains
                  itself in both places (AC3). */}
              <Tooltip
                label="Configure session dates and time slots before uploading groups"
                disabled={groupsPrereqsMet}
              >
                <Button leftSection={<Upload size={18} />} onClick={onUploadGroups} disabled={!groupsPrereqsMet}>
                  Upload Matched Groups
                </Button>
              </Tooltip>
              <Button variant="default" leftSection={<Plus size={18} />} onClick={openAddGroupModal} disabled={!groupsPrereqsMet}>
                Add Group
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Add Group Modal */}
        <Modal opened={addGroupModalOpened} onClose={closeAddGroupModal} title="Create New Group" size="sm">
          <Stack gap="md">
            <TextInput
              label="Group Name"
              placeholder="e.g., Group 1, Alpha Team"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.currentTarget.value)}
              required
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={closeAddGroupModal}>
                Cancel
              </Button>
              <Button onClick={handleCreateGroup} loading={creatingGroup}>
                Create Group
              </Button>
            </Group>
          </Stack>
        </Modal>
      </>
    );
  }

  // ─── Card view ─────────────────────────────────────────────────────
  if (displayMode === 'card') {
    return (
      <>
        {/* MED-829 / E23-8: AC14 — flag-gated matching-server panel above groups. */}
        {matchingServerEnabled && (
          <Stack gap="md" mb="md">
            <MatchingSessionsPanel
              cohortId={cohortId}
              groupsPrereqsMet={groupsPrereqsMet}
            />
            {/* AC13 — groups-list-level override warning when the latest
                completed sync had user_in_valid=true. */}
            {showGroupsListOverrideWarning && (
              <Alert
                icon={<AlertCircle size={18} />}
                color="warningAmber"
                variant="light"
                title="Override saved"
              >
                <Stack gap="xs" align="flex-start">
                  <Text size="sm">
                    The current groups were synced by a matching session that included one or more
                    constraint overrides. Review carefully.
                  </Text>
                  <OverrideWarning />
                </Stack>
              </Alert>
            )}
          </Stack>
        )}
        <GroupsCardView
          cohortId={cohortId}
          groups={groups}
          participants={participants}
          cardsPerRow={cardsPerRow}
          searchValue={searchValue}
          loadingMore={loadingMore}
          groupsPagination={groupsPagination}
          onLoadMore={onLoadMore}
          groupsData={{
            groupSessionsMap,
            cohortTimeSlots,
            calloutData,
            fetchCalloutData,
            participantMap,
            groupHealthMap,
          }}
          coachAssignment={coachAssignment}
          onRefresh={onRefresh}
          coachFeaturesEnabled={coachFeaturesEnabled}
          isPeerLed={isPeerLed}
          configuredGroupCoachingSessionCount={configuredGroupCoachingSessionCount}
        />
      </>
    );
  }

  // ─── Main render (list view) ──────────────────────────────────────
  return (
    <DndContext sensors={dragDrop.sensors} onDragStart={dragDrop.handleDragStart} onDragEnd={dragDrop.handleDragEnd}>
      <Stack gap="md">
        {/* MED-829 / E23-8: AC14 — flag-gated matching-server panel above groups. */}
        {matchingServerEnabled && (
          <MatchingSessionsPanel
            cohortId={cohortId}
            groupsPrereqsMet={groupsPrereqsMet}
          />
        )}

        {/* AC13 — groups-list-level override warning when the latest completed sync had user_in_valid=true. */}
        {showGroupsListOverrideWarning && (
          <Alert
            icon={<AlertCircle size={18} />}
            color="warningAmber"
            variant="light"
            title="Override saved"
          >
            <Stack gap="xs" align="flex-start">
              <Text size="sm">
                The current groups were synced by a matching session that included one or more
                constraint overrides. Review carefully.
              </Text>
              <OverrideWarning />
            </Stack>
          </Alert>
        )}

        {/* Coach Selection Bar */}
        {coachAssignment.isCoachSelectionMode && coachAssignment.selectedCoachForAssignment && (
          <CoachSelectionBar
            coach={coachAssignment.selectedCoachForAssignment}
            selectedCount={coachAssignment.selectedGroupIdsForAssignment.size}
            onCancel={coachAssignment.handleCancelCoachSelection}
            onAssign={openAssignCoachConfirmModal}
          />
        )}

        {/* Coach Assignment Callout Banners (EC-34, EC-36) */}
        {/* MED-1161: coach contracts/announcements don't apply to peer-led cohorts (AC7). */}
        {!isPeerLed && (
          <GroupCalloutBanners
            calloutData={calloutData}
            onSendAnnouncements={openAnnouncementModal}
            onManageCoaches={() => navigate(`/cohorts/${cohortId}?tab=coaches`)}
            isEmailDisabled={isCoachActivatedEmailDisabled}
          />
        )}

        {/* Coaches Panel */}
        {/* MED-1161: hide the coach availability/matching panel for peer-led cohorts (AC2). */}
        {!isPeerLed && (
          <CoachesPanel
            cohortId={cohortId}
            groups={groups}
            onSendReminder={handleSendCoachReminder}
            onCoachSelect={coachAssignment.handleCoachSelect}
            selectedCoachId={coachAssignment.selectedCoachForAssignment?.id}
            refreshTrigger={coachAssignment.coachPanelRefreshTrigger}
            groupSessionsMap={groupSessionsMap}
            configuredGroupCoachingSessionCount={configuredGroupCoachingSessionCount}
          />
        )}

        {/* Groups List */}
        <Stack gap="md">
          {/* Collapse/Expand All Toggle */}
          <Group justify="flex-start">
            <Button
              variant="subtle"
              size="xs"
              leftSection={allExpanded ? <ChevronsUp size={14} /> : <ChevronsDown size={14} />}
              onClick={() => setAllExpanded(!allExpanded)}
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          </Group>

          {/* No search results */}
          {isSearchActive && groups.length === 0 && (
            <Card withBorder p="xl">
              <Center>
                <Text c="dimmed" size="sm">No groups match your search.</Text>
              </Center>
            </Card>
          )}

          {/* Group Cards */}
          {groups.map((group, index) => {
            const isAvailableForCoach = coachAssignment.selectedCoachForAssignment
              ? coachAssignment.isCoachAvailableForGroup(coachAssignment.selectedCoachForAssignment, group)
              : true;
            const groupSessions = groupSessionsMap[group.id] || [];

            return (
              <GroupCard
                key={group.id}
                group={group}
                cohortId={cohortId}
                colorIndex={index}
                isPeerLed={isPeerLed}
                schedule={formatGroupSchedule(group)}
                sessions={transformSessionsForGroupCard(groupSessions, group.coach_name || null)}
                configuredSessionCount={configuredGroupCoachingSessionCount}
                participantMap={participantMap}
                defaultExpanded={coachAssignment.isCoachSelectionMode && !isAvailableForCoach ? false : allExpanded}
                onRenameGroup={handleRenameGroup}
                onChangeCoach={handleChangeCoach}
                onAddParticipant={handleAddParticipantToGroup}
                onDeleteGroup={handleDeleteGroup}
                onRemoveMember={participantAssignment.handleRemoveMemberFromGroup}
                onAddTimeSlot={handleAddTimeSlot}
                onEditTimeSlot={group.scheduled_time_slot_id ? handleEditTimeSlot : undefined}
                onManageSessions={() => navigate(`/cohorts/${cohortId}?tab=sessions`)}
                dragCount={participantAssignment.selectedParticipants.size > 0 ? participantAssignment.selectedParticipants.size : 1}
                isSelectionMode={coachAssignment.isCoachSelectionMode}
                isSelected={coachAssignment.selectedGroupIdsForAssignment.has(group.id)}
                isAvailableForSelection={isAvailableForCoach}
                onToggleSelection={() => coachAssignment.handleToggleGroupSelection(group.id)}
                healthStatus={groupHealthMap[group.id]}
                onViewParticipant={onViewParticipant}
                onResolveMemberFlag={handleResolveMemberFlag}
                onDismissMemberFlag={handleDismissMemberFlag}
                // MED-1353: a peer-led group has no coach, so no booking links.
                showBookingLinks={!isPeerLed}
              />
            );
          })}

          {/* Load More */}
          {groupsPagination?.hasMore && (
            <Stack align="center" gap={4} mt="xs">
              <Button
                variant="default"
                onClick={onLoadMore}
                loading={loadingMore}
              >
                Load More Groups
              </Button>
              <Text size="xs" c="dimmed">
                Showing {groups.length} of {groupsPagination.total} groups
              </Text>
            </Stack>
          )}
        </Stack>

        {/* Unassigned Participants - hidden during search */}
        {!isSearchActive && <UnassignedParticipantsSection
          participants={participantAssignment.unassignedParticipants}
          groups={groups}
          selectedParticipants={participantAssignment.selectedParticipants}
          onSelectParticipant={participantAssignment.handleSelectParticipant}
          onSelectAll={participantAssignment.handleSelectAll}
          onAssignToGroup={handleAssignToGroup}
          onBulkAssign={participantAssignment.handleBulkAssign}
          selectionMode={participantAssignment.selectionMode}
        />}
      </Stack>

      {/* Drag Overlay */}
      <DragOverlay>
        {dragDrop.activeDragParticipant && (
          <DraggableParticipantCard
            participant={dragDrop.activeDragParticipant}
            isDragOverlay
            colorScheme="medleyGreen"
          />
        )}
      </DragOverlay>

      {/* Assign to Group Modal */}
      <Modal opened={assignModalOpened} onClose={closeAssignModal} title="Assign to Group" size="sm">
        <Stack gap="md">
          {participantAssignment.selectedParticipantForAssign ? (
            <Text size="sm">
              Assign{' '}
              <strong>
                {participantAssignment.selectedParticipantForAssign.first_name}{' '}
                {participantAssignment.selectedParticipantForAssign.last_name}
              </strong>{' '}
              to:
            </Text>
          ) : (
            <Select
              label="Select Participant"
              placeholder="Choose a participant"
              data={participantAssignment.getParticipantSelectOptions()}
              value={null}
              onChange={participantAssignment.handleSelectParticipantFromDropdown}
              searchable
            />
          )}
          <Select
            label="Select Group"
            placeholder="Choose a group"
            data={participantAssignment.groupOptions}
            value={participantAssignment.selectedGroupId}
            onChange={participantAssignment.setSelectedGroupId}
            searchable
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeAssignModal}>
              Cancel
            </Button>
            <Button
              onClick={handleExecuteAssignment}
              loading={participantAssignment.assigning}
              disabled={!participantAssignment.selectedGroupId || !participantAssignment.selectedParticipantForAssign}
            >
              Assign
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Add Group Modal */}
      <Modal opened={addGroupModalOpened} onClose={closeAddGroupModal} title="Create New Group" size="sm">
        <Stack gap="md">
          <TextInput
            label="Group Name"
            placeholder="e.g., Group 1, Alpha Team"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.currentTarget.value)}
            required
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeAddGroupModal}>
              Cancel
            </Button>
            <Button onClick={handleCreateGroup} loading={creatingGroup}>
              Create Group
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Assign Coach Modal */}
      <AssignCoachModal
        opened={assignCoachModalOpened}
        onClose={() => {
          closeAssignCoachModal();
          coachAssignment.clearGroupForCoachAssign();
        }}
        cohortId={cohortId}
        group={coachAssignment.groupForCoachAssign}
        groupSessions={
          coachAssignment.groupForCoachAssign
            ? groupSessionsMap[coachAssignment.groupForCoachAssign.id]
            : undefined
        }
        onAssigned={async () => {
          await onRefresh();
          await fetchCalloutData();
        }}
      />

      {/* Send Coach Reminder Modal */}
      <SendCoachReminderModal
        opened={sendReminderModalOpened}
        onClose={() => {
          closeSendReminderModal();
          coachAssignment.clearCoachForReminder();
        }}
        cohortId={cohortId}
        coach={coachAssignment.coachForReminder}
        emailDisabled={isCoachAvailabilityEmailDisabled}
      />

      {/* Assign Coach to Groups Confirm Modal */}
      <AssignCoachConfirmModal
        opened={assignCoachConfirmModalOpened}
        onClose={closeAssignCoachConfirmModal}
        coach={coachAssignment.selectedCoachForAssignment}
        selectedGroupCount={coachAssignment.selectedGroupIdsForAssignment.size}
        groupsWithExistingCoach={coachAssignment.getGroupsWithExistingCoach()}
        assignmentWarnings={assignmentWarnings}
        onConfirm={handleAssignCoachToGroups}
      />

      {/* Add Time Slot Modal */}
      <AddTimeSlotModal
        opened={addTimeSlotModalOpened}
        onClose={() => {
          closeAddTimeSlotModal();
          setGroupForTimeSlot(null);
        }}
        cohortId={cohortId}
        group={groupForTimeSlot}
        timeSlots={cohortTimeSlots}
        onAssigned={async () => {
          await onRefresh();
        }}
      />

      {/* Edit Group Time Slot Modal (MED-648) */}
      <EditGroupTimeSlotModal
        opened={editTimeSlotModalOpened}
        onClose={() => {
          closeEditTimeSlotModal();
          setGroupForTimeSlot(null);
        }}
        cohortId={cohortId}
        group={groupForTimeSlot}
        timeSlots={cohortTimeSlots}
        onUpdated={async () => {
          await onRefresh();
        }}
      />

      {/* Send Announcements Modal (EC-34) */}
      <SendAnnouncementsModal
        opened={announcementModalOpened}
        onClose={closeAnnouncementModal}
        cohortId={cohortId}
        onSent={fetchCalloutData}
      />

      {/* Group Capacity Warning Modal (MED-448) */}
      <GroupCapacityWarningModal
        warning={dragDrop.capacityWarning}
        onConfirm={dragDrop.confirmCapacityOverride}
        onCancel={dragDrop.dismissCapacityWarning}
      />

      {/* MED-620: Confirmation modal for group reassignment in multi-modality cohorts */}
      <GroupReassignConfirmModal
        opened={reassignModalOpened}
        onClose={handleReassignCancel}
        onConfirm={handleReassignConfirm}
        info={reassignInfo}
        confirming={reassignConfirming}
      />

      {/* MED-714: Confirmation modal for group deletion */}
      <DeleteGroupConfirmModal
        opened={deleteGroupModalOpened}
        onClose={() => {
          closeDeleteGroupModal();
          setGroupToDelete(null);
        }}
        onConfirm={handleConfirmDeleteGroup}
        group={groupToDelete}
        deleting={deletingGroup}
      />

      {/* MED-1294: rename (list view — the card view mounts its own, since that
          mode returns early into GroupsCardView). */}
      <RenameGroupModal
        opened={renameGroupModalOpened}
        onClose={() => {
          closeRenameGroupModal();
          setGroupToRename(null);
        }}
        cohortId={cohortId}
        group={groupToRename}
        onRenamed={onRefresh}
      />
    </DndContext>
  );
}

export default GroupsView;
